import { api } from '../lib/api-client.js';

import type {
  CreateInspectionScheduleInput,
  InspectionScheduleCadence,
  InspectionScheduleScope,
  ListInspectionSchedulesQuery,
  UpdateInspectionScheduleInput,
} from '@sitewise/shared';

/* ----------------------------- Types --------------------------------- */

export interface InspectionScheduleSummary {
  id: string;
  orgId: string;
  templateId: string;
  buildingId: string | null;
  scope: InspectionScheduleScope;
  cadence: InspectionScheduleCadence;
  customIntervalDays: number | null;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  assignToUserId: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  isPaused: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RunScheduleResult {
  scheduleId: string;
  inspectionsCreated: number;
  nextRunAt: string | null;
}

/* ----------------------------- Helpers ------------------------------- */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/* ----------------------------- Calls --------------------------------- */

export async function listInspectionSchedules(
  filters: Partial<ListInspectionSchedulesQuery> = {},
) {
  return api<{ schedules: InspectionScheduleSummary[] }>(
    `/inspection-schedules${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchInspectionSchedule(id: string) {
  return api<{ schedule: InspectionScheduleSummary }>(`/inspection-schedules/${id}`);
}

export async function createInspectionSchedule(input: CreateInspectionScheduleInput) {
  return api<{ schedule: InspectionScheduleSummary }>('/inspection-schedules', {
    method: 'POST',
    body: input,
  });
}

export async function updateInspectionSchedule(
  id: string,
  input: UpdateInspectionScheduleInput,
) {
  return api<{ schedule: InspectionScheduleSummary }>(`/inspection-schedules/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteInspectionSchedule(id: string) {
  await api(`/inspection-schedules/${id}`, { method: 'DELETE' });
}

export async function pauseInspectionSchedule(id: string) {
  return api<{ schedule: InspectionScheduleSummary }>(`/inspection-schedules/${id}/pause`, {
    method: 'POST',
  });
}

export async function resumeInspectionSchedule(id: string) {
  return api<{ schedule: InspectionScheduleSummary }>(`/inspection-schedules/${id}/resume`, {
    method: 'POST',
  });
}

export async function runInspectionScheduleNow(id: string) {
  return api<RunScheduleResult>(`/inspection-schedules/${id}/run-now`, {
    method: 'POST',
  });
}
